/* ==========================================================================
   VAN LYNN — animation-controller.js
   IntersectionObserver-driven reveals, split-text headings, rAF parallax
   that only runs while elements are on screen. Does nothing unless the
   <html> element has .motion-ok (animations enabled and motion allowed).
   ========================================================================== */
(function () {
  'use strict';

  const html = document.documentElement;
  window.__vlAnimReady = true;
  const motionOk = () => html.classList.contains('motion-ok');

  /* ---------- Split text into words (accessible) ---------- */
  function splitWords(el) {
    if (el.dataset.splitDone) return;
    el.dataset.splitDone = 'true';
    const label = el.textContent.trim().replace(/\s+/g, ' ');
    let w = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const parts = child.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(' '));
              return;
            }
            const outer = document.createElement('span');
            outer.className = 'split-word';
            outer.setAttribute('aria-hidden', 'true');
            const inner = document.createElement('span');
            inner.className = 'split-word__inner';
            inner.style.setProperty('--w', w++);
            inner.textContent = part;
            outer.appendChild(inner);
            frag.appendChild(outer);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === Node.ELEMENT_NODE && !child.classList.contains('split-word')) {
          walk(child);
        }
      });
    };
    walk(el);
    const sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = label;
    el.prepend(sr);
  }

  /* ---------- Reveal observer ---------- */
  let revealObserver;
  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    return revealObserver;
  }

  function initReveals(root = document) {
    const targets = root.querySelectorAll('[data-reveal], [data-reveal-stagger], [data-split]');
    if (!motionOk() || !('IntersectionObserver' in window) || html.classList.contains('shopify-design-mode')) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    root.querySelectorAll('[data-split]').forEach(splitWords);
    root.querySelectorAll('[data-reveal-stagger]').forEach((group) => {
      Array.from(group.children).forEach((child, i) => child.style.setProperty('--i', i % 12));
    });
    const obs = getRevealObserver();
    targets.forEach((el) => {
      if (!el.classList.contains('is-revealed')) obs.observe(el);
    });
  }

  /* ---------- Parallax & scroll scale (only visible elements) ---------- */
  const active = new Set();
  let rafId = null;
  let parallaxObserver;

  function tick() {
    const vh = window.innerHeight;
    active.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2); // -1..1
      if (el.hasAttribute('data-parallax')) {
        const speed = parseFloat(el.dataset.parallax || '0.12');
        const y = Math.max(-1, Math.min(1, progress)) * speed * rect.height * -0.5;
        el.style.setProperty('--parallax-y', `${y.toFixed(2)}px`);
      }
      if (el.hasAttribute('data-scale-on-scroll')) {
        const p = Math.max(0, Math.min(1, 1 - (rect.top / vh)));
        const from = parseFloat(el.dataset.scaleFrom || '0.86');
        el.style.setProperty('--scroll-scale', (from + (1 - from) * p).toFixed(4));
      }
    });
    rafId = active.size ? window.requestAnimationFrame(tick) : null;
  }

  function initParallax(root = document) {
    if (!motionOk() || !html.classList.contains('parallax-on') || html.classList.contains('prefers-reduced-motion')) return;
    if (!('IntersectionObserver' in window)) return;
    if (!parallaxObserver) {
      parallaxObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) active.add(entry.target);
          else active.delete(entry.target);
        });
        if (active.size && !rafId) rafId = window.requestAnimationFrame(tick);
      }, { rootMargin: '10% 0px' });
    }
    root.querySelectorAll('[data-parallax], [data-scale-on-scroll]').forEach((el) => parallaxObserver.observe(el));
  }

  function init(root) {
    initReveals(root);
    initParallax(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);

  document.addEventListener('vl:content-loaded', (e) => init(e.detail?.root || document));
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  // Respond if the visitor changes their OS motion preference mid-session.
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  mq.addEventListener?.('change', () => {
    if (mq.matches && html.dataset.reducedMotion === 'respect') {
      html.classList.remove('motion-ok');
      document.querySelectorAll('[data-reveal], [data-reveal-stagger], [data-split]').forEach((el) => el.classList.add('is-revealed'));
      active.clear();
    }
  });

  window.VL = window.VL || {};
  window.VL.initAnimations = init;
})();
